// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  TableRow,
  TableCell,
  Table,
  Divider,
  Checkbox,
  ListItemText,
  ListItem,
  Chip,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
// import AppConfig from "appConfig";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  LabelList,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
  Legend,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { useTheme } from "@mui/material/styles";

function CallCenterInsightsPage() {
  const pageTitle = "Call Center Insights";
  usePageTitle(pageTitle);
  const { ref } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [callCenter, setCallCenter] = useState(null);
  const [insights, setInsights] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [currency, setCurrency] = useState(null);

  const colors = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "00:00:00";

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    return [
      h.toString().padStart(2, "0"),
      m.toString().padStart(2, "0"),
      s.toString().padStart(2, "0"),
    ].join(":");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/voice/call_centers/${ref}/insights.json`)
        .then((result) => {
          setCurrency(result.data.currency);
          console.log(result.data.data.analysis);
          setShowLoading(false);
          if (result.data.status === "success") {
            setCallCenter(result.data.data.call_center);
            setInsights(result.data.data.insights);
            setAnalysis(result.data.data.analysis);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  const statusKeys = Array.from(
    new Set(
      insights?.groups?.status?.flatMap((row) => Object.keys(row).filter((k) => k !== "status"))
    )
  );

  const outcomeKeys = Array.from(
    new Set(
      insights?.groups?.outcomes?.flatMap((row) => Object.keys(row).filter((k) => k !== "outcome"))
    )
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox mt={0} mb={1.5} display="flex" justifyContent="space-between">
              <MDBox>
                <Button
                  color="dark"
                  size="small"
                  startIcon={<Icon>west</Icon>}
                  onClick={() => {
                    navigate(`/voice/call-centers/${ref}`);
                  }}
                >
                  Back
                </Button>
              </MDBox>
              <MDBox align="right" pr={2}>
                <MDTypography variant="body2" fontSize={12}>
                  Call Center
                </MDTypography>
                <MDTypography variant="h6">{callCenter?.title}</MDTypography>
              </MDBox>
            </MDBox>

            <MDBox mt={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={2.4} lg={2.4}>
                  <MDBox sx={{ height: "100%" }}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography fontSize={24}>
                          <Icon>call</Icon>
                        </MDTypography>
                        <MDTypography variant="h4">{insights.totals.total_calls}</MDTypography>
                        <MDTypography variant="caption">Total Calls</MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={2.4} lg={2.4}>
                  <MDBox sx={{ height: "100%" }}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography fontSize={24}>
                          <Icon>phone_callback</Icon>
                        </MDTypography>
                        <MDTypography variant="h4">{insights.totals.inbound_calls}</MDTypography>
                        <MDTypography variant="caption">Incoming Calls</MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={2.4} lg={2.4}>
                  <MDBox sx={{ height: "100%" }}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography fontSize={24}>
                          <Icon>phone_forwarded</Icon>
                        </MDTypography>
                        <MDTypography variant="h4">{insights.totals.outbound_calls}</MDTypography>
                        <MDTypography variant="caption">Outgoing Calls</MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={2.4} lg={2.4}>
                  <MDBox sx={{ height: "100%" }}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography fontSize={24}>
                          <Icon>access_time</Icon>
                        </MDTypography>
                        <MDTypography variant="h4">
                          {formatDuration(insights.totals.total_duration)}
                        </MDTypography>
                        <MDTypography variant="caption">Talk Time</MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={2.4} lg={2.4}>
                  <MDBox sx={{ height: "100%" }}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography fontSize={24}>
                          <Icon>av_timer</Icon>
                        </MDTypography>
                        <MDTypography variant="h4">
                          {formatDuration(insights.averages.avg_duration.toFixed(0))}
                        </MDTypography>
                        <MDTypography variant="caption">Avg Time</MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox mt={4}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox>
                    <Card>
                      <MDBox p={2}>
                        <MDBox align="left">
                          <MDTypography variant="h6">Call Outcomes</MDTypography>
                        </MDBox>
                        <MDBox mt={1}>
                          <ResponsiveContainer width="100%" height={240}>
                            <BarChart
                              data={insights.breakdowns.outcomes}
                              layout="vertical"
                              barCategoryGap="10%"
                            >
                              <XAxis type="number" tick={{ fontSize: 12 }} />
                              <YAxis dataKey="outcome" type="category" tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Bar
                                dataKey="count"
                                minPointSize={5}
                                fill={theme.palette.dark.main}
                              ></Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox>
                    <Card>
                      <MDBox p={2}>
                        <MDBox align="left">
                          <MDTypography variant="h6">Call Status</MDTypography>
                        </MDBox>
                        <MDBox mt={1}>
                          <ResponsiveContainer width="100%" height={240}>
                            <BarChart
                              data={insights.breakdowns.status}
                              layout="vertical"
                              barCategoryGap="10%"
                            >
                              <XAxis type="number" tick={{ fontSize: 12 }} />
                              <YAxis dataKey="status" type="category" tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Bar dataKey="count" minPointSize={5}>
                                {insights.breakdowns.status.map((entry, index) => {
                                  let color = theme.palette.grey[400];

                                  if (entry.status === "completed")
                                    color = theme.palette.success.main;
                                  else if (entry.status === "missed")
                                    color = theme.palette.error.main;
                                  else if (entry.status === "dropped")
                                    color = theme.palette.dark.main;

                                  return <Cell key={index} fill={color} />;
                                })}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
            {analysis && (
              <MDBox mt={5}>
                <MDBox mb={3} align="center">
                  <MDTypography variant="h6">AI Analysis</MDTypography>
                  <Divider />
                </MDBox>
                <MDBox>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3} lg={3}>
                      <MDBox>
                        <Card>
                          <MDBox p={2} textAlign="center">
                            <MDTypography variant="h3">
                              {analysis.scoring.avg_score.toFixed(2)}%
                            </MDTypography>
                            <MDTypography variant="caption">Score</MDTypography>
                          </MDBox>
                        </Card>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={9} lg={9}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={2.4} lg={2.4}>
                          <Card>
                            <MDBox p={2} textAlign="center">
                              <MDTypography mt={2} variant="h5">
                                {analysis.scoring.avg_professionalism.toFixed(2)}%
                              </MDTypography>
                              <MDTypography variant="caption">Professionalism</MDTypography>
                            </MDBox>
                          </Card>
                        </Grid>
                        <Grid item xs={6} md={2.4} lg={2.4}>
                          <Card>
                            <MDBox p={2} textAlign="center">
                              <MDTypography mt={2} variant="h5">
                                {analysis.scoring.avg_clarity.toFixed(2)}%
                              </MDTypography>
                              <MDTypography variant="caption">Clarity</MDTypography>
                            </MDBox>
                          </Card>
                        </Grid>
                        <Grid item xs={6} md={2.4} lg={2.4}>
                          <Card>
                            <MDBox p={2} textAlign="center">
                              <MDTypography mt={2} variant="h5">
                                {analysis.scoring.avg_empathy.toFixed(2)}%
                              </MDTypography>
                              <MDTypography variant="caption">Empathy</MDTypography>
                            </MDBox>
                          </Card>
                        </Grid>
                        <Grid item xs={6} md={2.4} lg={2.4}>
                          <Card>
                            <MDBox p={2} textAlign="center">
                              <MDTypography mt={2} variant="h5">
                                {analysis.scoring.avg_listening.toFixed(2)}%
                              </MDTypography>
                              <MDTypography variant="caption">Listening</MDTypography>
                            </MDBox>
                          </Card>
                        </Grid>
                        <Grid item xs={6} md={2.4} lg={2.4}>
                          <Card>
                            <MDBox p={2} textAlign="center">
                              <MDTypography mt={2} variant="h5">
                                {analysis.scoring.avg_control.toFixed(2)}%
                              </MDTypography>
                              <MDTypography variant="caption">Call Control</MDTypography>
                            </MDBox>
                          </Card>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid mt={3} item xs={12}>
                    <MDBox>
                      <Card>
                        <MDBox p={2}>
                          <MDTypography mb={3} variant="h6">
                            Performance Breakdown
                          </MDTypography>

                          <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                              data={[
                                {
                                  name: "Professionalism",
                                  score: analysis.scoring.avg_professionalism,
                                },
                                {
                                  name: "Clarity",
                                  score: analysis.scoring.avg_clarity,
                                },
                                {
                                  name: "Empathy",
                                  score: analysis.scoring.avg_empathy,
                                },
                                {
                                  name: "Listening",
                                  score: analysis.scoring.avg_listening,
                                },
                                {
                                  name: "Call Control",
                                  score: analysis.scoring.avg_control,
                                },
                              ]}
                            >
                              <CartesianGrid strokeDasharray="3 3" />

                              <XAxis dataKey="name" />

                              <YAxis />

                              <Tooltip />

                              <Legend />

                              <Bar dataKey="score" fill="#4caf50" radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </MDBox>
                      </Card>
                    </MDBox>
                  </Grid>
                </MDBox>
                <MDBox mt={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4} lg={4}>
                      <MDBox sx={{ height: "100%" }}>
                        <Card sx={{ height: "100%" }}>
                          <MDBox p={2}>
                            <MDTypography mb={3} variant="h6">
                              Sentiment Distribution
                            </MDTypography>

                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={analysis.sentiment}
                                  dataKey="total"
                                  nameKey="sentiment"
                                  innerRadius={70}
                                  outerRadius={100}
                                  paddingAngle={3}
                                >
                                  {analysis.sentiment.map((entry, index) => {
                                    const colors = [
                                      "#f44336",
                                      "#ff9800",
                                      "#9e9e9e",
                                      "#4caf50",
                                      "#2e7d32",
                                    ];

                                    return (
                                      <Cell key={index} fill={colors[index % colors.length]} />
                                    );
                                  })}
                                </Pie>

                                <Tooltip />

                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </MDBox>
                        </Card>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={8} lg={8}>
                      <MDBox sx={{ height: "100%" }}>
                        <Card sx={{ height: "100%" }}>
                          <MDBox p={2}>
                            <MDTypography mb={3} variant="h6">
                              Performance Trend
                            </MDTypography>

                            <ResponsiveContainer width="100%" height={300}>
                              <LineChart
                                data={
                                  analysis.trends.find((x) => x.name === "Performance Trend")
                                    ?.data || []
                                }
                              >
                                <CartesianGrid strokeDasharray="3 3" />

                                <XAxis dataKey="day" />

                                <YAxis />

                                <Tooltip />

                                <Legend />

                                <Line
                                  type="monotone"
                                  name="Daily Average Score"
                                  dataKey="avg_score"
                                  stroke="#2196f3"
                                  strokeWidth={3}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </MDBox>
                        </Card>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            )}
            <MDBox mt={5}>
              <MDBox mb={3} align="center">
                <MDTypography variant="h6">Contacts</MDTypography>
                <Divider />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} lg={4}>
                  <MDBox sx={{ height: "100%" }}>
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2} textAlign="center">
                        <MDTypography fontSize={24}>
                          <Icon>person</Icon>
                        </MDTypography>
                        <MDTypography variant="h3">{insights.contacts.total}</MDTypography>
                        <MDTypography variant="caption">CONTACTS REACHED</MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                {insights.contacts.unique_per_direction.map((direction) => (
                  <Grid item xs={12} md={4} lg={4}>
                    <MDBox sx={{ height: "100%" }}>
                      <Card sx={{ height: "100%" }}>
                        <MDBox p={2} textAlign="center">
                          <MDTypography fontSize={24}>
                            <Icon>
                              {direction.direction === "inbound"
                                ? "phone_callback"
                                : "phone_forwarded"}
                            </Icon>
                          </MDTypography>
                          <MDTypography variant="h3">{direction.total}</MDTypography>
                          <MDTypography variant="caption">
                            {direction.direction.toUpperCase()}
                          </MDTypography>
                        </MDBox>
                      </Card>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
            <MDBox mt={5}>
              <MDBox mb={3} align="center">
                <MDTypography variant="h6">Financials</MDTypography>
                <Divider />
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={5} lg={5}>
                  <MDBox sx={{ height: "100%" }}>
                    <MDBox>
                      <Card>
                        <MDBox p={2} textAlign="center">
                          <MDTypography fontSize={24}>{currency}</MDTypography>
                          <MDTypography variant="h6" fontSize={24}>
                            {`${(insights.financials.total_spent / 100).toLocaleString("en", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`}
                          </MDTypography>
                          <MDTypography variant="caption">Total Spend</MDTypography>
                        </MDBox>
                      </Card>
                    </MDBox>
                    <MDBox mt={1.5}>
                      <Card>
                        <MDBox p={2} textAlign="center">
                          <MDTypography fontSize={24}>{currency}</MDTypography>
                          <MDTypography variant="h6" fontSize={24}>
                            {`${(insights.averages.avg_charge / 100).toLocaleString("en", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`}
                          </MDTypography>
                          <MDTypography variant="caption">Avg Spend</MDTypography>
                        </MDBox>
                      </Card>
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={7} lg={7}>
                  <Card>
                    <MDBox p={2}>
                      <MDTypography variant="h6">Spend Breakdown</MDTypography>
                      <MDBox>
                        <ResponsiveContainer width="100%" height={240}>
                          <PieChart>
                            <Pie
                              data={insights.financials.spend_per_direction}
                              dataKey="total"
                              nameKey="direction"
                              outerRadius={70}
                              animationDuration={600}
                              labelLine={false}
                              label={({ name, percent, x, y }) => (
                                <text
                                  x={x}
                                  y={y}
                                  fontSize={12}
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  fill="#666"
                                >
                                  {`${name} (${(percent * 100).toFixed(0)}%)`}
                                </text>
                              )}
                            >
                              {insights.financials.spend_per_direction.map((entry, index) => (
                                <Cell key={index} fill={colors[index % colors.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) =>
                                `${currency}${(value / 100).toLocaleString("en", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                              }
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
            <MDBox mt={5}>
              <MDBox mb={3} align="center">
                <MDTypography variant="h6">Team Performance</MDTypography>
                <Divider />
              </MDBox>
              <MDBox>
                <Card>
                  <MDBox p={2}>
                    <MDTypography mb={3} variant="h6">
                      Calls Breakdown
                    </MDTypography>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart height={300} data={insights.groups.calls.total} barSize={12}>
                        <XAxis dataKey="group" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill={theme.palette.dark.main} />
                      </BarChart>
                    </ResponsiveContainer>
                  </MDBox>
                </Card>
              </MDBox>
              <Grid mt={3} container spacing={3}>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox>
                    <Card>
                      <MDBox p={2}>
                        <MDTypography mb={3} variant="h6">
                          Inbound Calls
                        </MDTypography>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart barSize={12} data={insights.groups.calls.inbound}>
                            <XAxis dataKey="group" />
                            <YAxis />
                            <Tooltip />

                            <Bar dataKey="value" fill={theme.palette.warning.main} />
                          </BarChart>
                        </ResponsiveContainer>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox>
                    <Card>
                      <MDBox p={2}>
                        <MDTypography mb={3} variant="h6">
                          Outbound Calls
                        </MDTypography>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart barSize={12} data={insights.groups.calls.outbound}>
                            <XAxis dataKey="group" />
                            <YAxis />
                            <Tooltip />

                            <Bar dataKey="value" fill={theme.palette.dark.main} />
                          </BarChart>
                        </ResponsiveContainer>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
              <Grid mt={3} container spacing={3}>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox>
                    <Card>
                      <MDBox p={2}>
                        <MDTypography mb={3} variant="h6">
                          Call Status
                        </MDTypography>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart barSize={12} data={insights.groups.status}>
                            <XAxis dataKey="status" />
                            <YAxis />
                            <Tooltip />
                            <Legend />

                            {statusKeys.map((key, i) => (
                              <Bar
                                key={key}
                                dataKey={key}
                                stackId="a"
                                fill={`hsl(${i * 45}, 65%, 55%)`}
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <MDBox>
                    <Card>
                      <MDBox p={2}>
                        <MDTypography mb={3} variant="h6">
                          Call Outcome
                        </MDTypography>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart barSize={12} data={insights.groups.outcomes}>
                            <XAxis dataKey="outcome" />
                            <YAxis />
                            <Tooltip />
                            <Legend />

                            {outcomeKeys.map((group, i) => (
                              <Bar
                                key={group}
                                dataKey={group}
                                stackId="a"
                                fill={`hsl(${i * 45}, 65%, 55%)`}
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default CallCenterInsightsPage;
